import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Check, X, FileText, MessageSquare } from "lucide-react";
import api, { API, authToken } from "../../lib/api";
import { StatusMark } from "../../components/StatusMark";
import { ExportMenu } from "../../components/ExportMenu";
import { formatDate, formatApiErrorDetail, formatPT } from "../../lib/format";

export default function ReefRequests() {
  const [tab, setTab] = useState("scenes");
  const [subStatus, setSubStatus] = useState("pending");
  const [msgStatus, setMsgStatus] = useState("open");
  const [subs, setSubs] = useState([]);
  const [msgs, setMsgs] = useState([]);

  const load = useCallback(async () => {
    const [s, m] = await Promise.all([
      api.get(`/submissions${subStatus ? `?status=${subStatus}` : ""}`),
      api.get(`/messages${msgStatus ? `?status=${msgStatus}` : ""}`),
    ]);
    setSubs(s.data);
    setMsgs(m.data);
  }, [subStatus, msgStatus]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const subAct = async (id, action) => {
    try {
      await api.post(`/submissions/${id}/${action}`);
      toast.success(action === "approve" ? "Approved — added to your sides to assign." : "Declined.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const resolveMsg = async (id) => {
    try {
      await api.post(`/messages/${id}/resolve`);
      toast.success("Marked handled.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8" data-testid="reef-requests">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">From your actors — triage, don't babysit</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">Requests & Questions</h1>
          <p className="font-sans text-sm text-stone mt-2 max-w-xl">
            Everything students submit lands here. Clear it when you can — you're never obligated to reply to each one.
          </p>
        </div>
        <ExportMenu
          list="requests"
          params={{ status: tab === "scenes" ? subStatus : msgStatus }}
          title="Requests & Questions"
          columns={[
            { key: "who", label: "Name", render: (r) => r.student_name },
            { key: "what", label: "Detail", render: (r) => r.title || r.body || "" },
            { key: "status", label: "Status" },
            { key: "created_at", label: "Date", render: (r) => formatPT(r.created_at) },
          ]}
          rows={tab === "scenes" ? subs : msgs}
        />
      </div>

      <div className="flex gap-2 border-b border-hairline">
        <button onClick={() => setTab("scenes")} className={`px-4 py-2 font-sans text-sm border-b-2 -mb-px transition-colors ${tab === "scenes" ? "border-brass text-ink" : "border-transparent text-stone hover:text-ink"}`} data-testid="req-tab-scenes">
          Scene proposals
        </button>
        <button onClick={() => setTab("questions")} className={`px-4 py-2 font-sans text-sm border-b-2 -mb-px transition-colors ${tab === "questions" ? "border-brass text-ink" : "border-transparent text-stone hover:text-ink"}`} data-testid="req-tab-questions">
          Questions & updates
        </button>
      </div>

      {tab === "scenes" ? (
        <div className="space-y-4">
          <div className="flex gap-2">
            {["pending", "approved", "declined"].map((s) => (
              <button key={s} onClick={() => setSubStatus(s)} className={`px-3 py-1 text-xs font-sans capitalize border ${subStatus === s ? "border-brass text-ink bg-brass/10" : "border-hairline text-stone"}`} data-testid={`sub-filter-${s}`}>{s}</button>
            ))}
          </div>
          {subs.map((s) => (
            <div key={s.id} className="paper-card p-5" data-testid={`reef-submission-${s.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <FileText size={16} className="text-brass" />
                    <p className="font-serif text-xl text-ink leading-tight">{s.title}</p>
                  </div>
                  <p className="font-mono text-xs text-stone mt-1">
                    {s.student_name} · {s.type === "upload" ? "PDF attached" : "Request"} · {formatDate(s.created_at)}
                    {s.class_title ? ` · ${s.class_title}` : ""}
                  </p>
                  {s.note && <p className="font-sans text-sm text-ink/75 mt-2">{s.note}</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {s.storage_path && (
                    <button onClick={() => window.open(`${API}/submissions/${s.id}/download?auth=${authToken()}`, "_blank")} className="btn-ghost text-sm" data-testid={`view-submission-${s.id}`}>View PDF</button>
                  )}
                  {s.status === "pending" ? (
                    <>
                      <button onClick={() => subAct(s.id, "approve")} className="btn-brass text-sm px-4 py-2" data-testid={`approve-submission-${s.id}`}><Check size={14} /> Approve</button>
                      <button onClick={() => subAct(s.id, "decline")} className="btn-outline text-sm px-3 py-2 !border-garnet !text-garnet" data-testid={`decline-submission-${s.id}`}><X size={14} /></button>
                    </>
                  ) : (
                    <StatusMark status={s.status} />
                  )}
                </div>
              </div>
            </div>
          ))}
          {subs.length === 0 && <p className="font-sans text-sm text-stone">Nothing here.</p>}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex gap-2">
            {["open", "resolved"].map((s) => (
              <button key={s} onClick={() => setMsgStatus(s)} className={`px-3 py-1 text-xs font-sans capitalize border ${msgStatus === s ? "border-brass text-ink bg-brass/10" : "border-hairline text-stone"}`} data-testid={`msg-filter-${s}`}>{s}</button>
            ))}
          </div>
          {msgs.map((m) => (
            <div key={m.id} className="paper-card p-5 flex flex-col sm:flex-row sm:items-start justify-between gap-4" data-testid={`reef-message-${m.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <MessageSquare size={16} className="text-brass" />
                  <span className="font-mono text-[10px] uppercase tracking-wider text-brass">{m.kind}</span>
                  <span className="font-serif text-lg text-ink">{m.student_name}</span>
                </div>
                <p className="font-sans text-sm text-ink/80 mt-2">{m.body}</p>
                <p className="font-mono text-xs text-stone mt-1">{formatDate(m.created_at)}{m.class_title ? ` · ${m.class_title}` : ""}</p>
              </div>
              {m.status === "open" ? (
                <button onClick={() => resolveMsg(m.id)} className="btn-outline text-sm shrink-0" data-testid={`resolve-message-${m.id}`}><Check size={14} /> Mark handled</button>
              ) : (
                <StatusMark status="approved" labelOverride="Handled" />
              )}
            </div>
          ))}
          {msgs.length === 0 && <p className="font-sans text-sm text-stone">Nothing here.</p>}
        </div>
      )}
    </div>
  );
}
